import { useCallback, useEffect, useState } from "react";
import { ExportAccountsOptions } from "types/enums";
import { ExportCompany, UrlResponse } from "../../../../types/apiTypes";
import { SerializedError } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { CustomToast } from "../../CustomToast";
import { getErrorMessage } from "../../../../utils/getErrorMessage";
import { INPUT_REGEX } from "../../../../utils/regex";
import { ReuseModal } from "components/ui/ReuseModal";
import { Box, Stack, Typography } from "@mui/material";
import { SubBlock } from "../../export-data/SubBlock";
import { SelectedExportOptions } from "../../export-data/SelectedExportOption";
import { SelectCompanies } from "../../export-data/SelectCompanies";
import { ReuseInput } from "../../ReuseInput";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { openLinkHandler } from "../../../../utils/openLinkHandler";

const DEFAULT_FILE_NAME = "Export-data";

export const ExportCSVModal = ({
  title,
  description,
  submitBtnText,
  isLoading,
  items,
  onClose,
  onSubmit,
}: {
  title: string;
  description: string;
  submitBtnText: string;
  isLoading: boolean;
  items: ExportCompany[];
  onClose: () => void;
  onSubmit: (
    ids: string[],
    fileName: string,
    isSelectedAll: boolean,
  ) => Promise<UrlResponse>;
}) => {
  const [isError, setIsError] = useState<boolean>(false);
  const [fileName, setFileName] = useState<string>(DEFAULT_FILE_NAME);
  const [option, setOption] = useState<string>(ExportAccountsOptions.all);
  const [value, setValue] = useState<string>("");
  const [selectedItemList, setSelectedItemList] = useState<string[]>([]);
  const [analyzedItemList, setAnalyzedItemList] = useState<string[]>([]);

  const isSelectedAll = option === ExportAccountsOptions.all;

  useEffect(() => {
    if (isSelectedAll) {
      setValue("");
      setSelectedItemList(analyzedItemList);
    }
  }, [option]);

  useEffect(() => {
    const analyzedList: string[] = [];
    items.forEach(({ is_analyzed, id }) => {
      if (is_analyzed) analyzedList.push(id);
    });
    setSelectedItemList(analyzedList);
    setAnalyzedItemList(analyzedList);
  }, [items]);

  const handleChange = useCallback(
    (item: string) => {
      setSelectedItemList((prev) => {
        let isInList = false;

        const newList = prev.filter((id) => {
          if (id === item) {
            isInList = true;
            return false;
          }

          return true;
        });

        !isInList && newList.push(item);
        return newList;
      });
    },
    [setSelectedItemList],
  );

  const selectAllHandler = () => {
    if (!items) return setSelectedItemList([]);
    setSelectedItemList(
      analyzedItemList.length === selectedItemList.length
        ? []
        : analyzedItemList,
    );
  };

  const successHandler = ({ url }: UrlResponse) => {
    openLinkHandler(url, fileName);
    onClose();
  };

  const errorHandler = (
    error: FetchBaseQueryError | SerializedError | undefined,
  ) => {
    toast.error(
      <CustomToast
        type={"error"}
        text={
          getErrorMessage(error).globalError ||
          "Something went wrong. Please try again"
        }
        title={"Export Data Failed"}
      />,
    );
  };

  const submitHandler = async () => {
    onSubmit(selectedItemList, fileName, isSelectedAll)
      .then(successHandler)
      .catch(errorHandler);
  };

  const inputChangeHandler = (value: string) => {
    setIsError(!value.trim() || !INPUT_REGEX.test(value));
    setFileName(value);
  };

  const isDisabled =
    isError ||
    (selectedItemList.length === 0 &&
      option === ExportAccountsOptions.selected) ||
    isLoading;

  return (
    <ReuseModal
      isOpen={true}
      isLoading={isLoading}
      onClose={onClose}
      title={title}
      onSubmit={submitHandler}
      maxWidth="540px"
      submitBtnColor="#1C3C6C"
      submitBtnText={submitBtnText}
      isDisabled={isDisabled}
      isDisabledCancelBtn={isDisabled}
    >
      <Stack gap="16px">
        <SubBlock title="" text={description} />
        <SelectedExportOptions
          option={option}
          setOption={setOption}
          analyzedItemList={analyzedItemList}
          selectedItemList={selectedItemList}
        />
        {option === ExportAccountsOptions.selected && (
          <SelectCompanies
            value={value}
            analyzedItemList={analyzedItemList}
            selectedItemList={selectedItemList}
            companyList={items || []}
            handleChange={handleChange}
            selectAllHandler={selectAllHandler}
            setValue={setValue}
          />
        )}

        <SubBlock title="File Name" text="What should be the CSV file name?" />
        <Stack
          direction="row"
          gap="8px"
          alignItems="center"
          pt="16px"
          paddingBottom="35px"
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
            }}
          >
            <ReuseInput
              name="name"
              handleInputChange={(key, value) => inputChangeHandler(value)}
              errorMessage={
                "Must contain only letters, numbers, and basic punctuation"
              }
              value={fileName}
              maxLength={50}
              label="File Name*"
              placeholder=" e.g., INFUSE"
              isError={isError}
              setErrorHandler={(key, value) => setIsError(value)}
            />
          </Box>

          <Typography
            component="span"
            variant="body1"
            sx={{ fontWeight: 500, lineHeight: "26px" }}
            color="#4C545B"
          >
            .csv
          </Typography>
        </Stack>
      </Stack>
    </ReuseModal>
  );
};
